import { Fragment, useState, useRef } from "react";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";

import { useSnackbar } from "notistack";
import makeStyles from "styles/muiStylesCompat/makeStyles";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import Paper from "@mui/material/Paper";

import ButtonPrimary from "~/components/Buttons/ButtonPrimary";
import { httpInviteUser } from "services/http/companies";
import { adminInviteEmailSchema } from "~/validation/contactFields";

const inviteSchema = yup.object({
  email: adminInviteEmailSchema(),
});

const useStyles = makeStyles((_theme) => ({
  content: { padding: 48, overflowX: "auto" },
  title: { paddingBottom: 32 },
}));

const UserInviteView = (_props) => {
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation();
  const classes = useStyles();
  const navigate = useNavigate();

  const [saving, setSaving] = useState(false);
  const [serverErrors, setServerErrors] = useState(null);
  const inviteInFlight = useRef(false);

  const {
    handleSubmit,
    control,
    formState: { errors, isValid },
  } = useForm({
    resolver: yupResolver(inviteSchema),
    mode: "onChange",
    defaultValues: { email: "" },
  });

  const onSubmit = ({ email }) => {
    if (inviteInFlight.current) {
      return;
    }
    inviteInFlight.current = true;
    setSaving(true);
    const request = {
      data: {
        email: email.toLowerCase(),
        status: 100,
      },
    };

    httpInviteUser(request).then(
      (response) => {
        if (response?.Successful === "False") {
          inviteInFlight.current = false;
          const errorMsg =
            "Sähköpostin lähetys epäonnistui. Yritä myöhemmin uudelleen.";
          enqueueSnackbar(errorMsg, {
            variant: "error",
            anchorOrigin: { vertical: "bottom", horizontal: "center" },
            autoHideDuration: 5000,
          });
          setServerErrors({ 400: errorMsg });
          setSaving(false);
          return;
        }
        enqueueSnackbar("Kutsu lähetetty", {
          variant: "success",
          anchorOrigin: { vertical: "bottom", horizontal: "center" },
          autoHideDuration: 2000,
        });
        setServerErrors(null);
        setSaving(false);
        navigate("/dashboard/users");
      },
      (error) => {
        inviteInFlight.current = false;
        let errorMsg =
          "Kutsun lähetys epäonnistui. Tarkista sähköpostiosoite.";
        if (error?.data?.errorCode === "ADMIN_EXISTS") {
          errorMsg =
            "Kutsua ei voi lähettää. Sähköpostiosoitteella on jo muita rooleja. Roolin vaihtamiseksi ota yhteyttä tukeen support@opentaxi.fi.";
        } else if (error?.data?.errorCode === "DRIVER_EXISTS") {
          errorMsg =
            "Kutsua ei voi lähettää. Sähköpostiosoitteella on jo kuljettajarooli. Roolin vaihtamiseksi ota yhteyttä tukeen support@opentaxi.fi.";
        } else if (error?.data?.errorCode === "CUSTOMER_EXISTS") {
          errorMsg =
            "Kutsua ei voi lähettää. Sähköpostiosoitteella on jo asiakastunnus. Käytä toista sähköpostiosoitetta tai pyydä käyttäjää poistamaan tilinsä palvelusta (mobiilisovellus -> omat tiedot -> Poista profiili)";
        } else if (error?.data?.errorCode === "EMAIL_SEND_FAILED") {
          errorMsg =
            "Sähköpostin lähetys epäonnistui. Yritä myöhemmin uudelleen.";
        } else if (error?.data?.errorCode === "INVITE_ALREADY_PENDING") {
          errorMsg =
            "Kutsu tälle osoitteelle on jo olemassa. Yritä lähettää se uudelleen.";
        } else if (error?.data?.detail) {
          errorMsg = error.data.detail;
        }

        enqueueSnackbar(errorMsg, {
          variant: "error",
          anchorOrigin: { vertical: "bottom", horizontal: "center" },
          autoHideDuration: 5000,
        });

        setServerErrors({ 400: errorMsg });
        setSaving(false);
      },
    );
  };

  return (
    <Paper className={classes.content} elevation={0} square>
      <Grid
        container
        className={classes.title}
        spacing={0}
        direction="row"
        sx={{ justifyContent: "space-between", alignItems: "center" }}
      >
        <Grid size={12}>
          <Typography variant="h4">{t("Kutsu admin käyttäjä")}</Typography>
        </Grid>
      </Grid>

      <Grid
        container
        spacing={0}
        component="form"
        noValidate
        onSubmit={handleSubmit(onSubmit)}
      >
        <Grid size={12}>
          <Typography variant="subtitle1">{t("Sähköpostiosoite")}</Typography>

          <Controller
            name="email"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                error={Boolean(errors.email)}
                helperText={errors.email?.message}
                id="invite-form-email"
                fullWidth
                margin="dense"
                required
                slotProps={{ inputLabel: { shrink: true } }}
                type="email"
                variant="outlined"
                value={field.value ?? ""}
                onChange={(e) => field.onChange(e.target.value.toLowerCase())}
              />
            )}
          />

          <ButtonPrimary
            type="submit"
            disabled={saving || !isValid}
            loading={saving}
          >
            {t("Kutsu")}
          </ButtonPrimary>

          {serverErrors ? (
            <Fragment>
              <Box>
                {Object.keys(serverErrors).map((key) => (
                  <Box key={key}>
                    <Typography variant="body1" color="error" gutterBottom>
                      {serverErrors[key]}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Fragment>
          ) : null}
        </Grid>
      </Grid>
    </Paper>
  );
};

export default UserInviteView;
